import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  SparklesIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  BoltIcon,
  UserGroupIcon,
  ArrowRightIcon,
  CommandLineIcon,
  LockClosedIcon,
  LockOpenIcon,
  CheckCircleIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import './LandingPage.css';

/* ═══════════════════════════════════════════════════════════════════════════
   HEALTH SCORE ANIMATED RING COMPONENT
   The "Business Health Score" — a single fluctuating number (0-100) that
   abstracts complex BI into a dopamine-triggering high score.
   ═══════════════════════════════════════════════════════════════════════════ */
function HealthScoreRing({ targetScore = 87, animate = false }) {
  const [displayScore, setDisplayScore] = useState(0);

  useEffect(() => {
    if (!animate) return;
    let frame;
    const duration = 2000;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayScore(Math.round(eased * targetScore));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, targetScore]);

  const circumference = 2 * Math.PI * 90; // r=90

  return (
    <div className="health-score-ring" style={{ '--score': animate ? targetScore : 0 }}>
      <svg width="200" height="200" viewBox="0 0 200 200">
        <defs>
          <linearGradient id="healthGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="50%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
        </defs>
        <circle className="ring-track" cx="100" cy="100" r="90" />
        <circle
          className={`ring-fill ${animate ? 'animate' : ''}`}
          cx="100" cy="100" r="90"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: animate
              ? circumference - (circumference * targetScore) / 100
              : circumference,
            transition: 'stroke-dashoffset 2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </svg>
      <div className="health-score-value">
        <div className="stat-counter text-5xl font-bold text-white">
          {displayScore}
        </div>
        <div className="text-sm text-force-glow/70 font-medium mt-1">Business Health</div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   "YES, AND..." INTERACTIVE DEMO
   Shows how Force translates generic user intent into data-driven action.
   ═══════════════════════════════════════════════════════════════════════════ */
function YesAndDemo() {
  const [phase, setPhase] = useState('idle'); // idle → typing → response → insight
  const [typedText, setTypedText] = useState('');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const userQuestion = 'How do I get more customers?';
  const hasStarted = useRef(false);

  const startDemo = useCallback(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    setPhase('typing');
    let i = 0;
    const typeInterval = setInterval(() => {
      i++;
      setTypedText(userQuestion.slice(0, i));
      if (i >= userQuestion.length) {
        clearInterval(typeInterval);
        setTimeout(() => setPhase('response'), 800);
        setTimeout(() => setPhase('insight'), 2400);
      }
    }, 60);
  }, []);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(startDemo, 600);
      return () => clearTimeout(timer);
    }
  }, [isInView, startDemo]);

  return (
    <div ref={ref} className="w-full max-w-2xl mx-auto">
      <div className="glass-card p-6 space-y-4">
        {/* Chat header */}
        <div className="flex items-center gap-2 pb-3 border-b border-force-border">
          <div className="w-2 h-2 rounded-full bg-force-emerald animate-pulse" />
          <span className="text-sm text-gray-400 font-medium">Force AI — Live Demo</span>
        </div>

        {/* User message */}
        <AnimatePresence>
          {phase !== 'idle' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-end"
            >
              <div className="chat-bubble chat-bubble--user" style={{ animationDelay: '0s', opacity: 1 }}>
                <p className="text-sm">
                  {typedText}
                  {phase === 'typing' && <span className="typing-cursor" />}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Generic response (crossed out) */}
        <AnimatePresence>
          {(phase === 'response' || phase === 'insight') && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex justify-start"
            >
              <div className="chat-bubble chat-bubble--ai" style={{ opacity: 1 }}>
                <p className="text-xs text-gray-500 line-through mb-2">
                  "Run ads, improve SEO, try social media..."
                </p>
                <p className="text-sm text-gray-300 italic">
                  <span className="text-force-accent font-medium">Generic answers won't help.</span> Let me check your data...
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Data-driven insight */}
        <AnimatePresence>
          {phase === 'insight' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex justify-start"
            >
              <div className="chat-bubble chat-bubble--ai" style={{ opacity: 1 }}>
                <div className="flex items-center gap-2 mb-2">
                  <SparklesIcon className="w-4 h-4 text-force-accent" />
                  <span className="text-xs font-semibold text-force-glow">Data-Driven Insight</span>
                </div>
                <p className="text-sm leading-relaxed">
                  To get more customers, we usually look at marketing.{' '}
                  <strong className="text-force-cyan">However</strong>, I noticed you lost{' '}
                  <span className="text-force-amber font-bold">15 customers</span> last week (high churn).
                  Keeping them would have earned{' '}
                  <span className="text-force-emerald font-bold">£500 more</span>.
                  Should we fix the "hole in the bucket" first?
                </p>
                <div className="mt-3 flex gap-2">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-force-accent/20 text-force-glow border border-force-accent/30">
                    Fix Churn First
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-force-cyan/10 text-force-cyan border border-force-cyan/20">
                    Show Retention Report
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FOUNDER MODE METRIC TOGGLE
   Maps jargon → plain English to remove intimidation.
   ═══════════════════════════════════════════════════════════════════════════ */
function FounderModeToggle() {
  const [founderMode, setFounderMode] = useState(false);

  const metrics = [
    { analyst: 'Churn Rate', founder: 'Customer Drop-off', value: '8.2%', trend: 'down', good: true },
    { analyst: 'CAC', founder: 'Cost to Buy a Customer', value: '£34', trend: 'down', good: true },
    { analyst: 'ROI', founder: 'Money Multiplier', value: '3.2×', trend: 'up', good: true },
    { analyst: 'MRR', founder: 'Monthly Cash Flow', value: '£12.4k', trend: 'up', good: true },
    { analyst: 'LTV', founder: 'Customer Lifetime Value', value: '£890', trend: 'up', good: true },
    { analyst: 'Burn Rate', founder: 'Money Going Out', value: '£8.1k', trend: 'down', good: false },
  ];

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Toggle */}
      <div className="flex items-center justify-center gap-4 mb-8">
        <span className={`text-sm font-medium transition-colors ${!founderMode ? 'text-white' : 'text-gray-500'}`}>
          Analyst Mode
        </span>
        <button
          id="founder-mode-toggle"
          onClick={() => setFounderMode(!founderMode)}
          className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
            founderMode ? 'bg-force-accent' : 'bg-force-border'
          }`}
        >
          <div
            className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-300 ${
              founderMode ? 'translate-x-7' : 'translate-x-0.5'
            }`}
          />
        </button>
        <span className={`text-sm font-medium transition-colors ${founderMode ? 'text-white' : 'text-gray-500'}`}>
          Founder Mode
        </span>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {metrics.map((metric, i) => (
          <motion.div
            key={metric.analyst}
            className="glass-card p-4 text-center"
            initial={false}
            animate={{ scale: [1, 1.02, 1] }}
            transition={{ delay: i * 0.05, duration: 0.3 }}
          >
            <div className="text-2xl font-bold text-white mb-1 stat-counter">
              {metric.value}
            </div>
            <div className="metric-morph">
              <AnimatePresence mode="wait">
                <motion.span
                  key={founderMode ? 'founder' : 'analyst'}
                  initial={{ opacity: 0, y: -8, filter: 'blur(4px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, y: 8, filter: 'blur(4px)' }}
                  transition={{ duration: 0.3 }}
                  className="text-sm text-gray-400 block"
                >
                  {founderMode ? metric.founder : metric.analyst}
                </motion.span>
              </AnimatePresence>
            </div>
            <div className={`text-xs mt-2 font-medium ${metric.good ? 'text-force-emerald' : 'text-red-400'}`}>
              {metric.trend === 'up' ? '↑' : '↓'} {metric.good ? 'Healthy' : 'Watch'}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   RPG LEVEL SYSTEM
   Progressive disclosure — treat business maturity like a video game.
   ═══════════════════════════════════════════════════════════════════════════ */
function RPGLevels() {
  const levels = [
    {
      level: 1,
      title: 'The Hustler',
      description: 'Connect your data sources',
      icon: RocketLaunchIcon,
      unlocked: true,
      features: ['Basic Dashboard', 'Project Creation', 'Task Lists'],
      color: 'from-force-emerald to-green-400',
      xp: '0 XP',
    },
    {
      level: 2,
      title: 'The Operator',
      description: 'View your first reports',
      icon: ChartBarIcon,
      unlocked: true,
      features: ['Analytics', 'AI Task Generation', 'Team Management'],
      color: 'from-force-cyan to-blue-400',
      xp: '500 XP',
    },
    {
      level: 3,
      title: 'The Strategist',
      description: 'Hit 20% project margins',
      icon: AdjustmentsHorizontalIcon,
      unlocked: false,
      features: ['Quantum Theory', 'Profit Simulator', 'Advanced Charts'],
      color: 'from-force-accent to-purple-400',
      xp: '2,000 XP',
    },
    {
      level: 4,
      title: 'The Tycoon',
      description: 'Master AI-powered execution',
      icon: SparklesIcon,
      unlocked: false,
      features: ['Cohort Analysis', 'Predictive AI', 'Full Automation'],
      color: 'from-force-amber to-orange-400',
      xp: '10,000 XP',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-5xl mx-auto">
      {levels.map((level, i) => {
        const Icon = level.icon;
        return (
          <motion.div
            key={level.level}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.15, duration: 0.5 }}
            className={`level-card glass-card p-6 ${
              level.unlocked ? 'level-card--active' : 'level-card--locked'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${level.color} flex items-center justify-center`}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              {level.unlocked ? (
                <LockOpenIcon className="w-5 h-5 text-force-emerald" />
              ) : (
                <LockClosedIcon className="w-5 h-5 text-gray-600" />
              )}
            </div>
            <div className="text-xs font-bold text-force-accent mb-1">LEVEL {level.level}</div>
            <h4 className="text-lg font-bold text-white mb-1">{level.title}</h4>
            <p className="text-sm text-gray-400 mb-4">{level.description}</p>
            <div className="space-y-1.5">
              {level.features.map((feat) => (
                <div key={feat} className="flex items-center gap-2 text-xs">
                  {level.unlocked ? (
                    <CheckCircleIcon className="w-3.5 h-3.5 text-force-emerald flex-shrink-0" />
                  ) : (
                    <LockClosedIcon className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                  )}
                  <span className={level.unlocked ? 'text-gray-300' : 'text-gray-600'}>{feat}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-force-border">
              <span className="text-xs font-medium text-gray-500">{level.xp}</span>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FEATURE CARDS
   Premium glassmorphism feature showcase.
   ═══════════════════════════════════════════════════════════════════════════ */
const FEATURES = [
  {
    icon: CpuChipIcon,
    title: 'AI-Powered Planning',
    description: 'Gemini-driven project analysis with success probability scoring and intelligent task generation.',
    gradient: 'from-force-accent to-indigo-400',
  },
  {
    icon: ChartBarIcon,
    title: 'Real-Time Analytics',
    description: 'Earned Value Management, burndown charts, and predictive timeline forecasting.',
    gradient: 'from-force-cyan to-blue-400',
  },
  {
    icon: BoltIcon,
    title: 'Autonomous Execution',
    description: 'Self-organising task workflows that adapt to your team\'s velocity and capacity.',
    gradient: 'from-force-amber to-yellow-400',
  },
  {
    icon: UserGroupIcon,
    title: 'Team Intelligence',
    description: 'Smart resource allocation, workload balancing, and cross-functional team insights.',
    gradient: 'from-force-emerald to-green-400',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Enterprise Security',
    description: 'SSO, RBAC, audit trails, and SOC 2 compliant infrastructure for your projects.',
    gradient: 'from-rose-500 to-pink-400',
  },
  {
    icon: CommandLineIcon,
    title: 'Integration Hub',
    description: 'Connect Slack, Jira, GitHub, Google Drive, and 40+ tools with one-click OAuth.',
    gradient: 'from-violet-500 to-purple-400',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN LANDING PAGE COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function LandingPage() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Stats counter animation
  const StatsCounter = ({ end, suffix = '', prefix = '' }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const inView = useInView(ref, { once: true });

    useEffect(() => {
      if (!inView) return;
      let frame;
      const duration = 2000;
      const start = performance.now();
      const tick = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(eased * end));
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frame);
    }, [inView, end]);

    return (
      <span ref={ref} className="stat-counter">
        {prefix}{count.toLocaleString()}{suffix}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-force-dark text-white overflow-x-hidden">
      {/* ─── NAVIGATION BAR ─── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrollY > 50
            ? 'bg-force-dark/90 backdrop-blur-xl border-b border-force-border shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-force-accent to-force-cyan flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold tracking-tight">Executive Force</span>
              <span className="text-xs text-gray-500 ml-2 hidden sm:inline">EF-Projex</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              id="nav-sign-in"
              onClick={() => navigate('/auth/login')}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
            >
              Sign In
            </button>
            <button
              id="nav-get-started"
              onClick={() => navigate('/onboarding')}
              className="px-5 py-2 text-sm font-medium bg-force-accent hover:bg-indigo-500 text-white rounded-lg transition-all duration-200 shadow-glow-indigo"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ─── SECTION 1: HERO ─── */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center px-6 pt-20 grid-pattern"
      >
        {/* Floating orbs */}
        <div className="landing-orbs">
          <div className="landing-orb landing-orb--1" />
          <div className="landing-orb landing-orb--2" />
          <div className="landing-orb landing-orb--3" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium bg-force-accent/10 text-force-glow border border-force-accent/20">
              <BoltIcon className="w-3.5 h-3.5" />
              AI-Powered Project Management
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-5xl md:text-7xl font-extrabold leading-tight mb-6"
          >
            <span className="text-white">Your AI</span>
            <br />
            <span className="text-gradient-force">Project Commander</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-xl md:text-2xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed"
          >
            Turn intent into execution. Zero learning curve.
            <br className="hidden md:block" />
            <span className="text-gray-300">The AI that understands what you actually need.</span>
          </motion.p>

          {/* Health Score Ring */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isHeroInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex justify-center mb-12"
          >
            <div className="animate-float">
              <HealthScoreRing targetScore={87} animate={isHeroInView} />
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              id="hero-start-mission"
              onClick={() => navigate('/onboarding')}
              className="cta-glow px-8 py-4 bg-gradient-to-r from-force-accent to-indigo-500 text-white text-lg font-semibold rounded-xl hover:shadow-glow-indigo transition-all duration-300 flex items-center gap-2"
            >
              Start Your Mission
              <RocketLaunchIcon className="w-5 h-5" />
            </button>
            <button
              id="hero-sign-in"
              onClick={() => navigate('/auth/login')}
              className="px-8 py-4 bg-transparent border border-force-border text-gray-300 text-lg font-medium rounded-xl hover:bg-force-surface hover:border-force-accent/30 transition-all duration-300"
            >
              Sign In
            </button>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-force-border rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-force-accent rounded-full mt-2 animate-bounce" />
          </div>
        </motion.div>
      </section>

      {/* Divider */}
      <div className="section-divider" />

      {/* ─── SECTION 2: "YES, AND..." DEMO ─── */}
      <section className="relative py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-force-cyan/10 text-force-cyan border border-force-cyan/20 mb-4">
              The "Yes, and..." Pattern
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Not Another <span className="text-gradient-cyan">Generic Chatbot</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Force doesn't give generic advice. It intercepts your question,
              checks your live data, and gives you a <strong className="text-white">data-driven answer</strong> you
              can act on immediately.
            </p>
          </motion.div>

          <YesAndDemo />
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── SECTION 3: FOUNDER MODE ─── */}
      <section className="relative py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-force-emerald/10 text-force-emerald border border-force-emerald/20 mb-4">
              Zero Intimidation
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Speak <span className="text-gradient-force">Your Language</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Toggle between Analyst and Founder mode.
              Same data — different language. Try it.
            </p>
          </motion.div>

          <FounderModeToggle />
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── SECTION 4: RPG LEVEL SYSTEM ─── */}
      <section className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-force-amber/10 text-force-amber border border-force-amber/20 mb-4">
              Progressive Mastery
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Level Up Your <span className="text-gradient-force">Business</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Complex features unlock as you grow. No cognitive overload.
              Master daily tasks before diving into cohort analysis.
            </p>
          </motion.div>

          <RPGLevels />
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── SECTION 5: FEATURE GRID ─── */}
      <section className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Enterprise-Grade <span className="text-gradient-force">Capabilities</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Everything you need, nothing you don't. Built for teams that ship.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="glass-card p-6 group"
                >
                  <div className="feature-icon-glow">
                    <div className={`relative z-10 w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-force-glow transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── SECTION 6: SOCIAL PROOF / STATS ─── */}
      <section className="relative py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: 2400, suffix: '+', label: 'Projects Delivered' },
              { value: 50, suffix: '%', label: 'Time Saved' },
              { value: 18000, suffix: '+', label: 'AI Insights Generated' },
              { value: 99, suffix: '.9%', label: 'Uptime' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                  <StatsCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ─── SECTION 7: FINAL CTA ─── */}
      <section className="relative py-24 px-6">
        <div className="landing-orbs">
          <div className="landing-orb landing-orb--2" style={{ bottom: '20%', left: '10%' }} />
          <div className="landing-orb landing-orb--3" style={{ top: '10%', right: '10%' }} />
        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6">
              Ready to <span className="text-gradient-force">Command</span> Your Projects?
            </h2>
            <p className="text-xl text-gray-400 mb-10">
              Join the teams turning intent into execution with AI-powered project management.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                id="cta-start-mission"
                onClick={() => navigate('/onboarding')}
                className="cta-glow px-10 py-5 bg-gradient-to-r from-force-accent to-indigo-500 text-white text-lg font-semibold rounded-xl hover:shadow-glow-indigo transition-all duration-300 flex items-center gap-3"
              >
                Start Your Mission
                <ArrowRightIcon className="w-5 h-5" />
              </button>
              <button
                id="cta-sign-in"
                onClick={() => navigate('/auth/login')}
                className="px-10 py-5 bg-transparent border border-force-border text-gray-300 text-lg font-medium rounded-xl hover:bg-force-surface transition-all duration-300"
              >
                Sign In
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="border-t border-force-border py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-force-accent to-force-cyan flex items-center justify-center">
              <SparklesIcon className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold text-gray-400">Executive Force AI</span>
          </div>
          <p className="text-xs text-gray-600">
            © {new Date().getFullYear()} Executive Force AI. Enterprise Project Management.
          </p>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/auth/login')} className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
              Sign In
            </button>
            <button onClick={() => navigate('/onboarding')} className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
              Get Started
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
